/**
 * @file selfBilledPo.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-26
 * @description Self-billed purchase orders — the one set of rules the PO
 *              editor, the planning boards and the server share (Cam,
 *              2026-10-12).
 *
 *   A SELF-BILLED PO is us buying raw materials from a customer: we (or a
 *   third party for us) collect them and take them to our own site or to
 *   storage somewhere else. So:
 *
 *   - Its customer is always "None — delivered to us" (stock): customer_id
 *     null, never raised from or linked to a customer order.
 *   - Where the goods go is chosen separately — the "Delivery / storage
 *     address": STOCK (our own site) or any contact. Required.
 *   - Both sides' delivery terms are open: the supplier's and ours, every
 *     value either list has (we collect and deliver, a third party collects
 *     and delivers, direct delivery, customer collection). Third-Party is
 *     still a pair, as everywhere.
 *   - It is on the planning boards on its delivery date — the bulk board when
 *     every line is a tonne line, the bag board otherwise — and can be given a
 *     haulier, driver, vehicle and times like any row. It never needs to be
 *     planned: an unplanned one does not hold up mill emails, driver
 *     paperwork or the office summary. It is never sent to a mill; driver
 *     paperwork leaves out the unplanned ones and says so.
 *
 *   Board rows have no order line, so each is keyed `sb:<PO id>:<index of the
 *   line in the PO's line_items>:<line token>` in the boards' row-id slot
 *   (order_line_id). The token is the line's stable `lineId` (given to every
 *   line of a self-billed PO when it is saved), else a hash of its item code
 *   and words — so a board write names the line it means, and is refused
 *   ("This PO changed — reload the board") if the PO has moved on since.
 *
 *   1.1.0 (2026-10-13, review): rows someone else carries (Direct Delivery,
 *   third-party, Customer Collection) are no delivery of ours — left out of
 *   driver paperwork, its warnings, the day's counts and Today's Deliveries
 *   (carriedByOthers). The PDF prints the delivery / storage address and
 *   instructions only when the seller delivers (selfBilledPdfDelivery).
 *
 *   1.2.0 (2026-10-14, Cam): an order on one of its option dates that is not
 *   its delivery date (utils/planningDay isOptionCandidate) is no delivery
 *   that day either — ourDeliveryRows, driverPaperworkRows and millEmailRows
 *   leave it out, so it counts only on the day it is planned for.
 *
 *   1.3.0 (2026-10-15, Cam): a redelivery's row (utils/redelivery — a
 *   refused order delivered again from where it is held) is never sent to a
 *   mill either: nothing is bought for it; and a board's bulk change sends its
 *   row key (plannableRowIds).
 *
 *   The server's utils/selfBilledPo.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { isPackWeight, lineWeightKg } from './lineUnits';
import { isOptionCandidate } from './planningDay';

export const SELF_BILLED_LABEL = 'Self-billed';

/** Where a self-billed PO's goods go: our own site, or a contact's address. */
export const DELIVERY_TO_STOCK = 'stock';
export const DELIVERY_TO_CONTACT = 'contact';

/** The pinned first option of the Delivery / storage address picker. */
export const STOCK_BADGE = 'STOCK';
export const STOCK_OPTION_VALUE = 'stock';
export const OUR_SITE_NAME = 'Excel Agri Ltd.';
export const OUR_SITE_LINES = ['The Old Grain Drier', 'Dolton Beacon', 'Winkleigh', 'Devon', 'EX19 8PS'];

export const ADDRESS_PLACEHOLDER = 'Choose the delivery / storage address…';
export const ADDRESS_REQUIRED_MESSAGE =
  'A self-billed PO needs its delivery / storage address — choose where the goods are going.';
export const CUSTOMER_FIXED_REASON =
  'A self-billed PO is us buying from a customer, so it has no customer of its own: None — delivered to us.';
export const SELF_BILLED_HINT =
  'Self-billed: we are buying from a customer and collecting it ourselves (or having it collected). '
  + 'It shows on the planning boards but never needs planning, and is never sent to a mill.';

/** What the boards say on a self-billed row's actions that do not apply to it. */
export const SELF_BILLED_BOARD_REASON = 'A self-billed PO goes on the board its units decide — it is not moved by hand.';
export const SELF_BILLED_DATE_REASON = 'A self-billed PO has no customer order — change its delivery date on the PO.';
export const SELF_BILLED_NO_ORDER_REASON = 'A self-billed PO has no customer order — open the PO from its number.';
export const SELF_BILLED_BADGE_TITLE = 'Self-billed: we are buying from a customer and collecting it. It never needs planning and is never sent to a mill.';

/** Both vocabularies, every value — a self-billed PO may take any of them. */
export const SELF_BILLED_SUPPLIER_TERMS = ['Collection', 'Delivery', 'Direct Delivery', 'Customer Collection', 'Third-Party Collection'];
export const SELF_BILLED_OUR_TERMS = ['Delivery', 'Direct Delivery', 'Customer Collection', 'Third-Party Delivery'];
const THIRD_PARTY_SUPPLIER = 'Third-Party Collection';
const THIRD_PARTY_OURS = 'Third-Party Delivery';
const CUSTOMER_COLLECTION = 'Customer Collection';
const DIRECT_DELIVERY = 'Direct Delivery';

/** Sales account codes whose products go on the bag board (as order lines do). */
export const BAG_SALES_CODES = ['207', '207A', '200N'];

const text = (v) => String(v == null ? '' : v).trim();

/** The address a STOCK delivery prints — our own site, our name first. */
export function stockAddressText() {
  return [OUR_SITE_NAME, ...OUR_SITE_LINES].join('\n');
}

/**
 * Why a self-billed PO's terms cannot be saved, or null. Both sides are
 * required, each from its own list; Third-Party Collection and Third-Party
 * Delivery go together.
 */
export function selfBilledTermsProblem(supplierTerms, ourTerms) {
  const s = text(supplierTerms);
  const o = text(ourTerms);
  if (!s) return 'Choose the supplier delivery terms.';
  if (!SELF_BILLED_SUPPLIER_TERMS.includes(s)) return `"${s}" is not a supplier delivery term.`;
  if (!o) return 'Choose our delivery terms — how the goods reach the delivery / storage address.';
  if (!SELF_BILLED_OUR_TERMS.includes(o)) return `"${o}" is not one of our delivery terms.`;
  if ((s === THIRD_PARTY_SUPPLIER) !== (o === THIRD_PARTY_OURS)) {
    return 'Third-Party Collection and Third-Party Delivery go together — choose both, or neither.';
  }
  return null;
}

/**
 * Who carries a self-billed PO when it is not us: 'Customer Collection'
 * (both sides), 'Direct Delivery' (the supplier's side — the seller takes it
 * to the delivery / storage address) or 'Third-party haulage'. Null when it is
 * ours to carry. Matches utils/lorryDay.otherCarrierOf on the board rows.
 */
export function selfBilledCarrier(supplierTerms, ourTerms) {
  const s = text(supplierTerms);
  const o = text(ourTerms);
  if (s === CUSTOMER_COLLECTION && o === CUSTOMER_COLLECTION) return 'Customer Collection';
  if (s === DIRECT_DELIVERY) return 'Direct Delivery';
  if (o === THIRD_PARTY_OURS || s === THIRD_PARTY_SUPPLIER) return 'Third-party haulage';
  return null;
}

/**
 * Why the SELF-BILLED switch cannot be changed on this PO, or null.
 * @param {object} p
 * @param {boolean} p.ready           the database update has run
 * @param {boolean} [p.on]            what it is now
 * @param {string}  [p.poType]        'haulage' for a PO-H
 * @param {boolean} [p.xeroMade]      a PO made in Xero
 * @param {string}  [p.status]        the PO's status
 * @param {boolean} [p.billed]        a bill is linked for its goods
 * @param {boolean} [p.forCustomerOrder] raised from / bought for a customer order
 */
export function selfBilledToggleBlock({ ready, on = false, poType = null, xeroMade = false, status = '', billed = false, forCustomerOrder = false } = {}) {
  if (!ready) return 'Self-billed POs arrive with a database update that has not been applied yet.';
  if (poType === 'haulage') return 'A haulage order (PO-H) follows its customer order — it cannot be self-billed.';
  if (xeroMade) return 'This PO was made in Xero — it cannot be made self-billed here.';
  const st = text(status).toUpperCase();
  if (st === 'DELETED' || st === 'VOIDED') return 'This PO is archived.';
  if (billed || st === 'BILLED') return `This PO is billed, so it stays ${on ? 'self-billed' : 'as it is'}.`;
  if (!on && forCustomerOrder) {
    return 'This PO is for a customer order. A self-billed PO is us buying from a customer, so it is never raised for an order.';
  }
  return null;
}

/**
 * Why a PO cannot be saved as self-billed, or null — { code, message }.
 * The server's 400s / 409s and the editor's checks are this one rule.
 */
export function selfBilledSaveProblem({ deliveryTo = null, deliveryContactId = null, customerId = null, forCustomerOrder = false, supplierTerms = null, ourTerms = null } = {}) {
  if (forCustomerOrder) {
    return { code: 'SELF_BILLED_HAS_ORDER', message: 'A self-billed PO is us buying from a customer, so it cannot be raised for a customer order.' };
  }
  if (customerId != null && customerId !== '') {
    return { code: 'SELF_BILLED_CUSTOMER', message: CUSTOMER_FIXED_REASON };
  }
  const kind = text(deliveryTo);
  if (kind !== DELIVERY_TO_STOCK && kind !== DELIVERY_TO_CONTACT) {
    return { code: 'SELF_BILLED_ADDRESS_REQUIRED', message: ADDRESS_REQUIRED_MESSAGE };
  }
  if (kind === DELIVERY_TO_CONTACT && !(Number.isInteger(Number(deliveryContactId)) && Number(deliveryContactId) > 0)) {
    return { code: 'SELF_BILLED_ADDRESS_REQUIRED', message: ADDRESS_REQUIRED_MESSAGE };
  }
  const terms = selfBilledTermsProblem(supplierTerms, ourTerms);
  if (terms) return { code: 'SELF_BILLED_TERMS', message: terms };
  return null;
}

/**
 * A PO line's identity on the boards: its stable `lineId`, else `h` and a
 * short hash of its item code and words (lines saved before ids were given).
 */
export function selfBilledLineToken(line) {
  const id = String((line && line.lineId) || '').replace(/[^A-Za-z0-9-]/g, '');
  if (id) return id;
  const text = `${String((line && line.itemCode) || '').trim().toUpperCase()}|${String((line && line.description) || '').trim()}`;
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return `h${h.toString(36)}`;
}

/** The boards' row id for line `index` of self-billed PO `poId` (`line` gives its token). */
export const selfBilledRowId = (poId, index, line = null) => `sb:${poId}:${index}:${selfBilledLineToken(line)}`;
export const isSelfBilledRowId = (id) => /^sb:\d+:\d+(:[A-Za-z0-9-]+)?$/.test(String(id == null ? '' : id));
/** { poId, index, token } from a row id, or null (token null on a key without one). */
export function parseSelfBilledRowId(id) {
  if (!isSelfBilledRowId(id)) return null;
  const [, po, idx, token] = String(id).split(':');
  return { poId: Number(po), index: Number(idx), token: token || null };
}

/** A new stable id for a self-billed PO's line. */
export function newSelfBilledLineId() {
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `l${Date.now().toString(36)}-${rand()}${rand()}`;
}

/**
 * The ids a board sends to PUT /planning/bulk-update: order lines (integers),
 * self-billed rows (sb: keys) and redelivery rows (rd: keys). Haulage jobs and anything else are left
 * for the caller.
 */
export function plannableRowIds(ids) {
  // A redelivery's row (utils/redelivery — `rd:<redelivery id>:<order line id>`)
  // goes as its key too; its pattern is written here, not imported, because
  // utils/redelivery reaches this file through transportLock.
  const isRowKey = (id) => isSelfBilledRowId(id) || /^rd:\d+:\d+$/.test(String(id == null ? '' : id));
  return [...(ids || [])].filter(id => isRowKey(id) || Number.isInteger(Number(id)))
    .map(id => (isRowKey(id) ? String(id) : Number(id)));
}

/**
 * Which board a self-billed PO goes on: 'bag' when any of its goods lines is
 * counted in packs (its own unit weight, else its item's, known and not a
 * tonne) or is a bagged product (sales account 207 / 207A / 200N), 'bulk'
 * otherwise — every line a tonne line, or of no known weight (as an order line
 * of unknown weight counts as bulk). Decided per PO, so its lines never split
 * across the two boards (a PO is collected once, at one time).
 * @param {Array<{ unitWeightKg?, itemUnitWeightKg?, salesAccountCode? }>} lines
 */
export function selfBilledBoard(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const bag = list.some((l) => {
    if (!l) return false;
    if (BAG_SALES_CODES.includes(text(l.salesAccountCode).toUpperCase())) return true;
    return isPackWeight(lineWeightKg(l.unitWeightKg, l.itemUnitWeightKg));
  });
  return bag ? 'bag' : 'bulk';
}

/**
 * Is this board row someone else's to carry — no delivery of ours? A Customer
 * Collection row, or a self-billed PO that the seller delivers (Direct
 * Delivery) or a third party carries. They are left out of driver paperwork,
 * the day's counts and Today's Deliveries, as Direct Delivery and Third-Party
 * orders are (those never reach a board at all — except a third-party order
 * planned there with its linked haulier, `third_party_board`, which is a
 * delivery like any outside haulier's row: in the loads, paperwork and mill
 * emails, 26 Oct 2026).
 */
export function carriedByOthers(row) {
  if (!row) return false;
  if (row.is_customer_collection) return true;
  return !!(row.is_self_billed && selfBilledCarrier(row.supplier_delivery_terms, row.excel_agri_delivery_terms));
}

/**
 * The rows that are deliveries of ours ON THE DAY: carriedByOthers left out,
 * and an option-date candidate (planned on another of its option dates).
 */
export const ourDeliveryRows = (rows) => (rows || []).filter(r => !carriedByOthers(r) && !isOptionCandidate(r));

/**
 * What a self-billed PO's PDF says about where the goods go (Cam's supplier's
 * copy rule): the delivery / storage address and instructions only when the
 * seller delivers (supplier terms Delivery or Direct Delivery); otherwise who
 * collects, and nothing about where they are taken.
 * @returns {{ sellerDelivers: boolean, collectedBy: string|null }}
 */
export function selfBilledPdfDelivery(supplierTerms) {
  const s = text(supplierTerms);
  if (s === 'Delivery' || s === DIRECT_DELIVERY) return { sellerDelivers: true, collectedBy: null };
  if (s === THIRD_PARTY_SUPPLIER) return { sellerDelivers: false, collectedBy: 'Collected by a haulier for Excel Agri' };
  return { sellerDelivers: false, collectedBy: 'Collected by Excel Agri' };
}

/**
 * Is a self-billed board row planned? Someone else carries it (Customer
 * Collection, Direct Delivery, third-party haulage), or it has a haulier,
 * driver or vehicle of ours. Other rows: always true (not this rule's).
 */
export function selfBilledRowPlanned(row) {
  if (!row || !row.is_self_billed) return true;
  if (row.is_customer_collection) return true;
  if (selfBilledCarrier(row.supplier_delivery_terms, row.excel_agri_delivery_terms)) return true;
  return !!(text(row.haulier) || text(row.driver) || text(row.vehicle));
}

/** The PO numbers of the self-billed rows not planned, each once, in board order. */
export function unplannedSelfBilledPos(rows) {
  const seen = [];
  for (const r of rows || []) {
    if (!r || !r.is_self_billed || selfBilledRowPlanned(r)) continue;
    const n = text(r.purchase_order_number) || `PO #${r.purchase_order_id}`;
    if (!seen.includes(n)) seen.push(n);
  }
  return seen;
}

/**
 * The rows driver paperwork prints: every row but an unplanned self-billed
 * one, and none that someone else carries (a self-billed PO the seller
 * delivers or a third party carries; Customer Collection is left out by the
 * callers, as it always was) — nor an option-date candidate, which is
 * planned on another day.
 */
export const driverPaperworkRows = (rows) => (rows || []).filter(r => !isOptionCandidate(r) && !(r && r.is_self_billed
  && (!selfBilledRowPlanned(r) || selfBilledCarrier(r.supplier_delivery_terms, r.excel_agri_delivery_terms))));

/**
 * Mill / supplier order summaries never carry a self-billed PO, nor a
 * redelivery (nothing is bought — it is collected from where it is held), nor
 * an option-date candidate (the mill loads it on the day it is planned for).
 */
export const millEmailRows = (rows) => (rows || []).filter(r => !(r && (r.is_self_billed || r.is_redelivery)) && !isOptionCandidate(r));

/**
 * The send window's warning about unplanned self-billed POs, or null.
 * @param {Array} rows   the board's rows
 * @param {'driver'|'office'} kind
 */
export function unplannedSelfBilledWarning(rows, kind) {
  const pos = unplannedSelfBilledPos(rows);
  if (!pos.length) return null;
  const one = pos.length === 1;
  const which = one ? `${pos[0]} (self-billed) is` : `${pos.length} self-billed POs (${pos.join(', ')}) are`;
  if (kind === 'office') {
    return `${which} not planned — ${one ? 'it is' : 'they are'} on the summary with no haulier, driver or vehicle. Self-billed POs never need planning, so you can send anyway.`;
  }
  return `${which} not planned and won't be on the driver paperwork. Self-billed POs never need planning, so you can send anyway.`;
}
